/**
 * Orquesta el panel admin, API calls y el catálogo de permisos.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { useData } from '../DataContext';
import { useNavigation } from '../NavigationContext';

import ReportLibrary from './admin/ReportLibrary';
import ClientProjectManagement from './admin/ClientProjectManagement';
import NotificationPanel from './admin/NotificationPanel'; // Importar el nuevo componente
import ReportDetailView from './admin/ReportDetailView'; // Importar el nuevo componente
import StatisticsPanel from './admin/StatisticsPanel'; // Importar el nuevo componente de estadísticas
import UserStatisticsPanel from './admin/UserStatisticsPanel'; // Importar el nuevo componente de estadísticas de usuario
import ReportViewsModal from './admin/ReportViewsModal';
import ReportAccessUsersModal from './admin/ReportAccessUsersModal';
import OpsHub from './admin/OpsHub';
import EmailTemplatesPanel from './admin/EmailTemplatesPanel';
import ReportInventoryPanel from './admin/ReportInventoryPanel';
import ReportAdjustmentsHub from './admin/ReportAdjustmentsHub';
import ReportCreatorShell from './admin/ReportCreatorShell'; // Inicio (biblioteca) + editor del Creador
import SocialProfilesAdmin from './admin/SocialProfilesAdmin'; // Perfiles visibles del repositorio de Redes Sociales
import SocialRepository from './repos/SocialRepository'; // Módulo de consulta del repositorio de Redes Sociales
import ReportCopyModal from './admin/ReportCopyModal'; // Importar el nuevo modal de copia
import DeadlineEditModal from './admin/DeadlineEditModal'; // Importar el nuevo modal de deadline
import ReportResponsiblesModal from './admin/ReportResponsiblesModal'; // Importar el nuevo componente
import PeopleHub, { PEOPLE_HUB_VIEWS } from './admin/PeopleHub';
import { studio } from './admin/studioUi';
import { toDatetimeLocalValue } from '../datetimeLocal';
import { useIsNarrow, isNarrowViewport } from '../useMediaQuery';

// Definiciones de estados iniciales (se exportan para que otros componentes puedan usarlos)
export const initialUserState = { nombre: '', empresa: '', email: '', password: '', rol: '', photoURL: '', area: '' };
export const initialReportState = { cliente: '', clientId: '', proyecto: '', projectId: '', reporteNombre: '', dashboardUrl: '', repositorio: '', corUrl: '', hasEditableUrl: false, editableUrl: '', versionYear: '', versionMonth: '', enfoque: 'Interno', approvalDeadline: '' };
export const initialProjectState = { nombre: '', clientId: '', repositorio: '', logoUrl: '' };
export const initialClientState = { nombre: '', logoUrl: '' };
export const initialRoleState = { id: '', name: '', description: '', permissions: {} };
export const initialPermissionState = { userId: '', reporteId: '', cliente: '', clientId: '', proyecto: '', projectId: '', reporteNombre: '', dashboardUrl: '', accessType: 'view' };
export const initialGroupState = { name: '', description: '' };

const AdminPanel = () => {
    const { user, userPermissions, userRole, fetchWithAuth, withBusy, startImpersonation, API_URL } = useAuth();
    const narrow = useIsNarrow();
    /* En móvil el Creador se abre solo si el usuario lo pide explícitamente. */
    const [creatorOnMobile, setCreatorOnMobile] = useState(false);
    const { clients, projects, dashboards, setDashboards, refreshAllData, notifications, setNotifications } = useData();
    const {
        adminSubView: subView,
        setAdminSubView: setSubView,
        setView,
        setSelectedUrl,
        handleNotificationNavigation,
        selectedReportForDetails,
        setSelectedReportForDetails
    } = useNavigation();
    // Define la URL base de tu API, usando la variable de entorno para producción
    const API_URL_INTERNAL = API_URL;
    const [users, setUsers] = useState([]);
    const [permisosActuales, setPermisosActuales] = useState([]);
    const [groups, setGroups] = useState([]);
    const [groupMembers, setGroupMembers] = useState([]);
    const [groupPermissions, setGroupPermissions] = useState([]);
    const [roles, setRoles] = useState([]); // Nuevo estado para roles
    // Estados para la gestión de reportes (movidos de ReportLibrary)
    const [editandoId, setEditandoId] = useState(null);
    const [nuevoMaestro, setNuevoMaestro] = useState({ ...initialReportState });    
    // Estados que se mantienen en AdminPanel porque son globales o controlan modales
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);

    // Estados para el modal de vistas de reporte
    const [showViewsModal, setShowViewsModal] = useState(false);
    const [reportViews, setReportViews] = useState({ reportName: '', views: [], timeSeries: [] });
    const [reportAccessUsers, setReportAccessUsers] = useState([]); // Estado para los usuarios con acceso al reporte
    // Estados para el modal de usuarios con acceso
    const [showAccessUsersModal, setShowAccessUsersModal] = useState(false);
    const [reportForAccessUsers, setReportForAccessUsers] = useState(null); // Reporte para el que se muestran los accesos
    // Estados para el modal de responsables
    // Estados para el modal de copia
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [reportToCopy, setReportToCopy] = useState(null);
    // Estados para el modal de edición de deadline
    const [showDeadlineModal, setShowDeadlineModal] = useState(false);
    const [reportForDeadlineEdit, setReportForDeadlineEdit] = useState(null);

    const areasList = [ // Mover areasList aquí para que sea accesible globalmente en AdminPanel
        'Data',
        'Medios',
        'Social',
        'Cuentas',
        'Sistemas',
        'Dirección',
        'Legal',
        'RH',
        'Diseño',
        'Otra'
    ];
    const [showResponsiblesModal, setShowResponsiblesModal] = useState(false);
    const [selectedReportForResponsibles, setSelectedReportForResponsibles] = useState(null);
    const [reportResponsibles, setReportResponsibles] = useState([]);
    const [reportAdjustments, setReportAdjustments] = useState([]);
    const [commentRefreshKey, setCommentRefreshKey] = useState(0); // NEW
    const [allPermissionsList, setAllPermissionsList] = useState([]); // Lista de todos los permisos posibles

    const triggerCommentRefresh = () => setCommentRefreshKey(k => k + 1); // NEW

    const cargarDatosGenerales = useCallback(() => {
        // Carga la lista COMPLETA de usuarios para la GESTIÓN DE USUARIOS (más datos, más restringido)
        if (userPermissions.admin_panel_users_view || userPermissions.admin_panel_permissions_view || userPermissions.admin_panel_groups_view || userPermissions.maintenance_execute_tasks) {
            fetchWithAuth(`${API_URL_INTERNAL}/admin/users`).then(res => res.json()).then(data => setUsers(data.users || [])).catch(err => console.error("Fallo al cargar usuarios:", err));
        }
        if (userPermissions.permissions_view_all || userPermissions.admin_panel_permissions_view) {
            fetchWithAuth(`${API_URL_INTERNAL}/admin/all-permissions`).then(res => res.json()).then(data => setPermisosActuales(data.permisos || [])).catch(err => console.warn("No se pudieron cargar todos los permisos directos:", err));
        }
        if (userPermissions.groups_view_all) {
            fetchWithAuth(`${API_URL_INTERNAL}/admin/groups`).then(res => res.json()).then(data => setGroups(data.groups || [])).catch(err => console.error("Fallo al cargar grupos:", err));
        }
        if (userPermissions.roles_view_all) {
            fetchWithAuth(`${API_URL_INTERNAL}/admin/roles`).then(res => res.json()).then(data => setRoles(data.roles || [])).catch(err => console.error("Fallo al cargar roles:", err));
        }
    }, [API_URL_INTERNAL, userPermissions, fetchWithAuth]);

    // Renombramos cargarTodo a cargarDatosGenerales para mayor claridad
    const cargarTodo = useCallback(() => {
        cargarDatosGenerales(); // This fetches admin-specific lists like all users, groups, roles
        refreshAllData(); // This comes from DataContext and refreshes dashboards, clients, projects
    }, [cargarDatosGenerales, refreshAllData]);

    const handleImpersonate = async (targetUser) => {
        // Wrapper to handle navigation after impersonation starts
        await startImpersonation(targetUser);
        setView('user');
        setSelectedUrl(null);
    };

    // Nueva función para cargar y establecer los usuarios con acceso a un reporte
    const fetchAndSetReportAccessUsers = useCallback(async (reportId) => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/report-access-users/${reportId}`);
            const data = await res.json();
            if (data.success) {
                setReportAccessUsers(data.accessUsers);
            } else {
                console.warn("Advertencia al cargar usuarios con acceso:", data.error);
                setReportAccessUsers([]);
            }
        } catch (error) {
            console.warn("Error de conexión al cargar usuarios con acceso:", error);
            setReportAccessUsers([]);
        }
    }, [fetchWithAuth, API_URL_INTERNAL]);

    // Nueva función para cargar y establecer los responsables de un reporte
    const fetchAndSetReportResponsibles = useCallback(async (reportId) => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/responsibles`);
            const data = await res.json();
            if (data.success) {
                setReportResponsibles(data.responsibles);
            } else {
                console.warn("Advertencia al cargar responsables:", data.error);
                setReportResponsibles([]);
            }
        } catch (error) {
            console.warn("Error de conexión al cargar responsables:", error);
            setReportResponsibles([]);
        }
    }, [fetchWithAuth, API_URL_INTERNAL]);

    const handleImpersonate_Original = async (targetUser) => {
        // Wrapper to handle navigation after impersonation starts
        await startImpersonation(targetUser);
        setView('user');
        setSelectedUrl(null);
    };

    useEffect(() => { 
        setSelectedUserForPermissions(null);
        setSelectedGroup(null); // Resetear grupo seleccionado al cambiar de vista        
    }, [subView]);

    // Cargar la lista completa de permisos disponibles (para RoleManagement)
    useEffect(() => {
        // Esto debería ser una lista estática o cargada de un archivo de configuración,
        // o de un endpoint si los permisos son dinámicamente definidos en el backend.
        // Por ahora, lo hardcodeamos como ejemplo.
        // En un sistema real, esto podría venir de un archivo JSON o de un endpoint /admin/available-permissions
        const allPossiblePermissions = generateAllPermissionsList(); // Función para generar la lista
        setAllPermissionsList(allPossiblePermissions);
    }, []);

    // Efecto para cargar datos asociados cuando se selecciona un reporte para ver en detalle.
    // Esto centraliza la carga de datos y soluciona el problema de que los responsables
    // no se cargaban al navegar desde una notificación.
    useEffect(() => {
        if (selectedReportForDetails && selectedReportForDetails.id) {
            // Si estamos en modo de edición para este reporte, no recargamos los datos
            // para evitar que el formulario se resetee con datos antiguos mientras se edita.
            if (editandoId === selectedReportForDetails.id) {
                return;
            }

            // Se ejecutan en paralelo para mejorar la velocidad de carga
            Promise.all([
                fetchAndSetReportResponsibles(selectedReportForDetails.id),
                fetchAndSetReportAccessUsers(selectedReportForDetails.id)
            ]);
        } else {
            // Limpiar los datos si no hay reporte seleccionado para evitar mostrar datos antiguos.
            setReportResponsibles([]);
            setReportAccessUsers([]);
            setReportAdjustments([]);
        }
    }, [selectedReportForDetails, editandoId, fetchAndSetReportResponsibles, fetchAndSetReportAccessUsers]); // La dependencia clave es el reporte seleccionado.

    // Efecto para mantener `selectedReportForDetails` sincronizado con la lista principal `dashboards`
    useEffect(() => {
        if (subView === 'report-details' && selectedReportForDetails) {
            const freshReport = dashboards.find(d => d.id === selectedReportForDetails.id);
            if (freshReport) {
                // Comparamos con stringify para evitar bucles de renderizado si el objeto es idéntico
                if (JSON.stringify(freshReport) !== JSON.stringify(selectedReportForDetails)) {
                    setSelectedReportForDetails(freshReport);
                }
            } else {
                // Si el reporte ya no se encuentra (eliminado, sin acceso), volvemos a la biblioteca
                setSubView('biblioteca');
                setSelectedReportForDetails(null);
            }
        }
    }, [dashboards, subView, selectedReportForDetails, setSubView, setSelectedReportForDetails]);

    useEffect(() => {
        // Este efecto se encarga de cargar los datos necesarios cuando cambia la pestaña o una selección interna.
        cargarDatosGenerales();
    }, [cargarDatosGenerales]); // La dependencia es la función `cargarTodo` en sí misma.

    const findClientName = (clientId) => clients.find(c => c.id === clientId)?.nombre || 'N/A';
    const findProjectName = (projectId) => projects.find(p => p.id === projectId)?.nombre || 'N/A';
    const findUserName = (userId) => users.find(u => u.uid === userId)?.nombre || 'N/A';

    // Inicia el modo de edición para un reporte.
    const handleEditReport = (report, options = {}) => {
        setNuevoMaestro({
            id: report.id,
            cliente: report.cliente,
            clientId: report.clientId,
            proyecto: report.proyecto,
            projectId: report.projectId,
            reporteNombre: report.reporteNombre,
            dashboardUrl: report.dashboardUrl,
            repositorio: report.repositorio,
            corUrl: report.corUrl,
            hasEditableUrl: report.hasEditableUrl,
            editableUrl: report.editableUrl,
            versionYear: report.versionYear,
            versionMonth: report.versionMonth,
            enfoque: report.enfoque || 'Interno',
            approvalDeadline: toDatetimeLocalValue(report.approvalDeadline),
            isCustom: !!(report.isCustom || report.customConfig),
        });
        setEditandoId(report.id);

        // Si la edición NO se inicia desde la vista de detalles, nos aseguramos de estar en la biblioteca.
        if (options.source !== 'details') {
            setSubView('biblioteca');
            setSelectedReportForDetails(null);
        }
    };

    // Guarda los cambios de un reporte (creación o edición)
    const handleSaveReport = async (reportPayload) => {
        return withBusy(async () => {
        try {
            const res = await fetchWithAuth(editandoId ? `${API_URL_INTERNAL}/admin/reportes-maestros/${editandoId}` : `${API_URL_INTERNAL}/admin/reportes-maestros`, {
                method: editandoId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(reportPayload)
            });
            if (res.ok) {
                alert('Reporte guardado con éxito.');
                handleFinishEditing(); // Salir del modo edición
                refreshAllData(); // Recargar todos los datos para reflejar los cambios
            } else {
                const d = await res.json().catch(() => ({ error: "Error desconocido al guardar reporte." }));
                alert(d.error);
            }
        } catch (error) {
            alert(`Error de conexión: ${error.message}`);
        }
        }, 'Guardando reporte…');
    };

    // Termina el modo de edición y resetea el formulario.
    const handleFinishEditing = () => {
        setEditandoId(null);
        setNuevoMaestro({ ...initialReportState });
        // No es necesario llamar a cargarTodo() aquí, se hará después de guardar
        // o el usuario cancela explícitamente.
    };

    const handleOpenDeadlineModal = (report) => {
        setReportForDeadlineEdit(report);
        setShowDeadlineModal(true);
    };

    const handleUpdateDeadline = async (reportId, newDeadline) => {
        return withBusy(async () => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/deadline`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ approvalDeadline: newDeadline || null })
            });
            if (res.ok) {
                alert('Deadline actualizado con éxito.');
                refreshAllData(); // Recargar todos los datos para reflejar el estado actualizado desde el backend.
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error);
            }
        } catch (error) {
            alert(`Error al actualizar el deadline: ${error.message}`);
            throw error; // Re-lanzar para que el modal maneje el estado de carga
        }
        }, 'Guardando deadline…');
    };

    const handleResetReportStatus = async (reportId) => {
        const report = dashboards.find(r => r.id === reportId);
        if (!report) {
            alert('Reporte no encontrado.');
            return false;
        }
    
        if (window.confirm(`¿Resetear deadline, bloqueo y aprobaciones de «${report.reporteNombre}»? Los responsables quedarán destildados. No se puede deshacer.`)) {
            return withBusy(async () => {
            try {
                const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/maintenance/reset-report-status/${reportId}`, {
                    method: 'POST',
                    body: JSON.stringify({ unapproveResponsibles: true, clearDeadline: true, clearLock: true }),
                });
                const data = await res.json();
                if (data.success) {
                    alert(data.message);
                    refreshAllData(); // Recargar datos para que la UI se actualice
                    return true; // Indicate success to the modal
                } else {
                    throw new Error(data.error);
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
                return false; // Indicate failure
            }
            }, 'Reseteando reporte…');
        }
        return false; // User cancelled
    };

    const handleDeleteReport = async (reportId) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este reporte maestro? Esto también eliminará todos los permisos asociados a él.")) {
            return withBusy(async () => {
            try {
                const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}`, {
                    method: 'DELETE',
                });
                if (res.ok) {
                    alert('Reporte maestro eliminado con éxito.');
                    cargarTodo(); // Refrescar la lista de dashboards desde App.js
                    setSelectedReportForDetails(null); // Limpiar el reporte de detalles si estaba seleccionado
                } else {
                    const errorData = await res.json();
                    alert(`Error al eliminar reporte maestro: ${errorData.error}`);
                }
            } catch (error) {
                alert(`Error de conexión al eliminar reporte maestro: ${error.message}`);
            }
            }, 'Eliminando reporte…');
        }
    };

    const handleOpenCopyModal = (report) => {
        setReportToCopy(report);
        setShowCopyModal(true);
    };

    const handleConfirmCopy = async (reportId, { newReportName, copyResponsibles }) => {
        return withBusy(async () => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/copy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newReportName, copyResponsibles })
            });

            if (res.ok) {
                const data = await res.json();
                alert(data.message || 'Reporte copiado con éxito.');
                cargarTodo(); // Refresh the library view
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error);
            }
        } catch (error) {
            alert(`Error al copiar el reporte: ${error.message}`);
            // Re-throw so the modal knows it failed and doesn't close automatically
            throw error;
        }
        }, 'Copiando reporte…');
    };

    // Simplificada: ahora solo establece el estado. La carga de datos se maneja con el useEffect.
    const handleViewReportDetails = (report) => {
        setSelectedReportForDetails(report);
        setSubView('report-details');
    };

    const handleMarkNotificationAsRead = async (notificationId) => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/notifications/${notificationId}/read`, {
                method: 'PUT',
            });
            if (res.ok) {
                setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
            } else {
                console.error("Error marking notification as read.");
            }
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    const handleMarkAllAsRead = async () => {
        if (!window.confirm('¿Estás seguro de que quieres marcar todas las notificaciones como leídas?')) {
            return;
        }
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/notifications/read-all`, {
                method: 'PUT',
            });
            if (res.ok) {
                // Actualización optimista en el frontend
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                alert('Todas las notificaciones han sido marcadas como leídas.');
            } else {
                console.error("Error al marcar todas las notificaciones como leídas.");
                alert('Hubo un error al marcar las notificaciones.');
            }
        } catch (error) {
            console.error("Error al marcar todas las notificaciones como leídas:", error);
            alert('Hubo un error de conexión.');
        }
    };

    const handleShowReportViews = async (report) => {
        const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/report-views/${report.id}`);
        const data = await res.json();
        if (data.success) {
            setReportViews({ reportName: report.reporteNombre, views: data.views || [], timeSeries: data.timeSeries || [] });
            setShowViewsModal(true);
        } else {
            alert("Error al cargar las vistas del reporte.");
        }
    };

    const handleShowAccessUsers = async (report) => { // Esta función se mantiene aquí porque controla el modal global
        setReportForAccessUsers(report); // ¡CRÍTICO! Establecer el reporte para el modal
        await fetchAndSetReportAccessUsers(report.id);
        setShowAccessUsersModal(true);
    };

    // Funciones para la gestión de responsables
    const handleUpdateResponsibleApproval = async (reportId, responsibleId, approved, options = {}) => {
        return withBusy(async () => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/responsibles/${responsibleId}/approve`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    approved,
                    acknowledgeLate: options.acknowledgeLate === true,
                })
            });
            if (res.ok) {
                const data = await res.json();
                alert(data.message || 'Actualización realizada con éxito.');
                await fetchAndSetReportResponsibles(reportId);
                cargarTodo(); // Recargar la biblioteca para actualizar el semáforo
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error);
            }
        } catch (error) {
            console.error("Error al actualizar estado de aprobación:", error);
            throw error; // Re-throw para que el modal o la vista lo manejen si es necesario
        }
        }, 'Guardando aprobación…');
    };

    const handleShowResponsiblesModal = async (report) => {
        setSelectedReportForResponsibles(report);
        await fetchAndSetReportResponsibles(report.id);
        setShowResponsiblesModal(true);
    };

    const handleAddResponsible = async (reportId, userId, area) => {
        return withBusy(async () => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/responsibles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, area })
            });
            if (res.ok) {
                alert('Responsable añadido con éxito.');
                // Recargar la lista de responsables en el estado
                await fetchAndSetReportResponsibles(reportId);
                cargarTodo(); // Recargar la biblioteca para actualizar el semáforo
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error);
            }
        } catch (error) {
            console.error("Error al añadir responsable:", error);
            throw error; // Re-throw para que el modal lo maneje
        }
        }, 'Guardando responsable…');
    };

    const handleRemoveResponsible = async (reportId, responsibleId) => {
        return withBusy(async () => {
        try {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${reportId}/responsibles/${responsibleId}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                alert('Responsable eliminado con éxito.');
                // Recargar la lista de responsables en el estado
                await fetchAndSetReportResponsibles(reportId);
                cargarTodo(); // Recargar la biblioteca para actualizar el semáforo
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error);
            }
        } catch (error) {
            console.error("Error al eliminar responsable:", error);
            throw error; // Re-throw para que el modal lo maneje
        }
        }, 'Eliminando responsable…');
    };

    const handleUpdateReportProperties = (reportId, updatedProperties) => {
        // Update the report in the main 'biblioteca' state
        setDashboards(prev => prev.map(r => 
            r.id === reportId ? { ...r, ...updatedProperties } : r
        ));
        // If the currently viewed detailed report is the one being updated, update it too
        if (selectedReportForDetails && selectedReportForDetails.id === reportId) {
            setSelectedReportForDetails(prev => ({ ...prev, ...updatedProperties }));
        }
    };

    const getFullTitle = (subView) => {
        const titles = {
            usuarios: 'Gestión de Usuarios',
            roles: 'Gestión de Usuarios',
            grupos: 'Gestión de Usuarios',
            permisos: 'Gestión de Usuarios',
            biblioteca: 'Biblioteca de Reportes',
            auditoria: 'Mantenimiento',
            clientes: 'Clientes y Proyectos',
            'report-details': 'Detalles del Reporte',
            notifications: 'Notificaciones',
            mantenimiento: 'Mantenimiento',
            'email-templates': 'Plantillas de correo',
            'inventario-reportes': 'Inventario de reportes',
            'estadisticas': 'Estadísticas de Reportes',
            'creador-reportes': 'Creador de Reportes personalizados',
            'repos-social': 'Redes Sociales',
            'repos-perfiles': 'Perfiles de Redes Sociales',
            'user-statistics': 'Estadísticas de Usuarios',
        };
        return titles[subView] || subView.toUpperCase().replace('-', ' ');
    };

    const fullTitle = getFullTitle(subView);

    // Lógica de filtrado para la vista de biblioteca
    let clientsForLibrary = clients;
    let projectsForLibrary = projects;

    if (userPermissions.admin_panel_library_view_own && !userPermissions.reports_view_all) {
        const accessibleClientIds = new Set(dashboards.map(r => r.clientId));
        const accessibleProjectIds = new Set(dashboards.map(r => r.projectId));
        clientsForLibrary = clients.filter(c => accessibleClientIds.has(c.id));
        projectsForLibrary = projects.filter(p => accessibleProjectIds.has(p.id));
    }

    const isPeopleHub = PEOPLE_HUB_VIEWS.includes(subView);
    const canSeePeopleHub = userPermissions.admin_panel_users_view || userPermissions.admin_panel_groups_view || userPermissions.admin_panel_roles_view || userPermissions.admin_panel_permissions_view;
    const canSeeOpsHub = userPermissions.maintenance_execute_tasks || userPermissions.admin_panel_audit_view || userPermissions.admin_panel_comprehensive_audit_view;
    const isOpsHub = subView === 'mantenimiento' || subView === 'auditoria';
    const canSeeEmailTemplates = userRole === 'admin' || userPermissions.admin_panel_email_templates;
    const canSeeInventory = userRole === 'admin' || userPermissions.admin_panel_report_inventory || userPermissions.admin_panel_library_view;
    const canSeeAdjustmentsHub = userRole === 'admin' || userPermissions.admin_panel_library_view || userPermissions.reports_manage_adjustments;
    const hideLegacyTitle = isPeopleHub || isOpsHub || subView === 'email-templates' || subView === 'inventario-reportes' || subView === 'ajustes-reportes' || subView === 'clientes' || subView === 'biblioteca' || subView === 'notifications' || subView === 'estadisticas' || subView === 'user-statistics' || subView === 'report-details' || subView === 'repos-perfiles' || subView === 'repos-social';

    return (
        <div style={{
            ...styles.container,
            ...(narrow ? styles.containerNarrow : null),
            ...(subView === 'creador-reportes' ? styles.creatorShell : {}),
            ...(subView === 'repos-social' ? (narrow ? styles.reposShellNarrow : styles.reposShell) : {})
        }}>
            {/* Modales */}
            <ReportViewsModal
                show={showViewsModal}
                onClose={() => setShowViewsModal(false)}
                reportViews={reportViews}
                users={users}
                findUserName={findUserName}
                styles={styles} // Pasar estilos si son compartidos
            />
            <ReportAccessUsersModal
                show={showAccessUsersModal}
                onClose={() => { setShowAccessUsersModal(false); setReportForAccessUsers(null); }}
                reportAccessUsers={reportAccessUsers}
                reportForAccessUsers={reportForAccessUsers}
                styles={styles} // Pasar estilos si son compartidos
            />
            <ReportResponsiblesModal
                show={showResponsiblesModal}
                onClose={() => { setShowResponsiblesModal(false); setSelectedReportForResponsibles(null); }}
                report={selectedReportForResponsibles}
                responsibles={reportResponsibles}
                allUsers={users} // Pasar todos los usuarios para la selección
                areasList={areasList} // Pasar la lista de áreas
                onAddResponsible={handleAddResponsible}
                onRemoveResponsible={handleRemoveResponsible}
                onUpdateApproval={handleUpdateResponsibleApproval}
                styles={styles}
                userPermissions={userPermissions}
                currentUserUid={user.uid}
                adjustments={reportAdjustments}
            />
            <ReportCopyModal
                show={showCopyModal}
                onClose={() => setShowCopyModal(false)}
                report={reportToCopy}
                onConfirm={handleConfirmCopy}
                styles={styles}
            />
            <DeadlineEditModal
                show={showDeadlineModal}
                onClose={() => setShowDeadlineModal(false)}
                report={reportForDeadlineEdit}
                onConfirm={handleUpdateDeadline}
                onReset={handleResetReportStatus}
                userPermissions={userPermissions}
                styles={styles}
            />

            <div style={subView === 'creador-reportes' ? styles.creatorWrap : { maxWidth: '1500px', margin: '0 auto' }}>
                {!hideLegacyTitle && (
                <h2 style={subView === 'creador-reportes' ? styles.creatorTitle : { marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px', fontSize: '1.7rem', fontWeight: '600', color: '#5E35B1' }}>
                    {fullTitle}
                </h2>
                )}
                {isPeopleHub && canSeePeopleHub && (
                    <PeopleHub
                        initialTab={subView === 'grupos' ? 'grupos' : subView === 'roles' ? 'roles' : subView === 'permisos' ? 'permisos' : 'usuarios'}
                        styles={styles}
                        userPermissions={userPermissions}
                        users={users}
                        roles={roles}
                        groups={groups}
                        setGroups={setGroups}
                        selectedGroup={selectedGroup}
                        setSelectedGroup={setSelectedGroup}
                        groupMembers={groupMembers}
                        setGroupMembers={setGroupMembers}
                        groupPermissions={groupPermissions}
                        setGroupPermissions={setGroupPermissions}
                        dashboards={dashboards}
                        cargarTodo={cargarTodo}
                        fetchWithAuth={fetchWithAuth}
                        API_URL={API_URL_INTERNAL}
                        areasList={areasList}
                        onImpersonate={handleImpersonate}
                        findUserName={findUserName}
                        allPermissionsList={allPermissionsList}
                        permisosActuales={permisosActuales}
                        setPermisosActuales={setPermisosActuales}
                        selectedUserForPermissions={selectedUserForPermissions}
                        setSelectedUserForPermissions={setSelectedUserForPermissions}
                    />
                )}
                {subView === 'clientes' && userPermissions.admin_panel_clients_projects_view && (
                    <ClientProjectManagement
                        clients={clients}
                        projects={projects}
                        biblioteca={dashboards}
                        cargarTodo={cargarTodo}
                        styles={styles}
                        fetchWithAuth={fetchWithAuth}
                        API_URL={API_URL_INTERNAL}
                        userPermissions={userPermissions}
                        findClientName={findClientName}
                        findProjectName={findProjectName}
                    />
                )}
                {subView === 'biblioteca' && (userPermissions.admin_panel_library_view || userPermissions.admin_panel_library_view_own) && (
                    <ReportLibrary
                        biblioteca={dashboards}
                        clients={clientsForLibrary}
                        projects={projectsForLibrary}
                        cargarTodo={cargarTodo}
                        fetchWithAuth={fetchWithAuth}
                        handleShowReportViews={handleShowReportViews}
                        handleShowAccessUsers={handleShowAccessUsers} // Se mantiene para mostrar usuarios con acceso
                        handleShowResponsiblesModal={handleShowResponsiblesModal} // Nuevo prop para mostrar responsables
                        styles={styles}
                        userPermissions={userPermissions}
                        onViewDetails={handleViewReportDetails} // Nuevo prop
                        onEditReport={handleEditReport} // Nuevo prop
                        onDeleteReport={handleDeleteReport}
                        onCopyReport={handleOpenCopyModal}
                        onSaveReport={handleSaveReport} // Nuevo prop para guardar
                        editandoId={editandoId} // Nuevo prop
                        setEditandoId={setEditandoId}
                        nuevoMaestro={nuevoMaestro} // Nuevo prop
                        setNuevoMaestro={setNuevoMaestro}
                        refreshDashboards={cargarTodo}
                        onFinishEditing={handleFinishEditing}
                    />
                )} 
                {subView === 'estadisticas' && userPermissions.admin_panel_statistics_view && (
                    <StatisticsPanel
                        styles={styles}
                        biblioteca={dashboards} // Pasamos la lista completa para buscar nombres de reportes
                        clients={clients}
                        projects={projects}
                        areasList={areasList}
                        fetchWithAuth={fetchWithAuth}
                        API_URL={API_URL_INTERNAL}
                    />
                )}
                {subView === 'user-statistics' && userPermissions.admin_panel_statistics_view && (
                    <UserStatisticsPanel
                        styles={styles}
                        fetchWithAuth={fetchWithAuth}
                        API_URL={API_URL_INTERNAL}
                        users={users}
                        areasList={areasList}
                    />
                )}
                {subView === 'notifications' && userPermissions.admin_panel_access && (
                    <NotificationPanel
                        notifications={notifications}
                        styles={styles}
                        onMarkAsRead={handleMarkNotificationAsRead}
                        onMarkAllAsRead={handleMarkAllAsRead} // Pasar la nueva función
                        onLinkClick={handleNotificationNavigation} // Usamos el manejador que viene de App.js
                    />
                )}
                {subView === 'report-details' && selectedReportForDetails && ( // Se muestra si hay un reporte seleccionado
                    <ReportDetailView
                        report={selectedReportForDetails}
                        onBack={() => { setSubView('biblioteca'); setSelectedReportForDetails(null); }}
                        onEdit={(report) => handleEditReport(report, { source: 'details' })}
                        onDelete={handleDeleteReport}
                        onCopy={handleOpenCopyModal} // Cambiado para abrir el modal
                        onShowViews={handleShowReportViews}
                        onOpenDeadlineModal={handleOpenDeadlineModal} // Nuevo prop para el modal de deadline
                        onShowAccessUsers={handleShowAccessUsers}
                        styles={styles}
                        userPermissions={userPermissions}
                        API_URL_INTERNAL={API_URL_INTERNAL}
                        fetchWithAuth={fetchWithAuth}
                        reportResponsibles={reportResponsibles} // Pasar la lista de responsables
                        reportAccessUsers={reportAccessUsers} // Pasar la lista de usuarios con acceso
                        onUpdateResponsibleApproval={handleUpdateResponsibleApproval} // Pasar la función de actualización
                        currentUserUid={user.uid} // Pasar el UID del usuario actual
                        onUpdateReportProperties={handleUpdateReportProperties} // Para actualizar estado de comentarios
                        handleShowResponsiblesModal={handleShowResponsiblesModal} 
                        currentUser={user} // Pasar el objeto de usuario completo para el panel de comentarios
                        userRole={userRole}
                        // Props para el formulario de edición en línea
                        editandoId={editandoId}
                        nuevoMaestro={nuevoMaestro}
                        setNuevoMaestro={setNuevoMaestro}
                        onSaveReport={handleSaveReport}
                        onFinishEditing={handleFinishEditing}
                        clients={clients}
                        projects={projects}
                        commentRefreshKey={commentRefreshKey} onClearComments={triggerCommentRefresh}
                        reportAdjustments={reportAdjustments}
                        onAdjustmentsChange={setReportAdjustments}
                    />
                )}
                {isOpsHub && canSeeOpsHub && (
                    <OpsHub
                        fetchWithAuth={fetchWithAuth}
                        API_URL={API_URL_INTERNAL}
                        API_URL_INTERNAL={API_URL_INTERNAL}
                        users={users}
                        dashboards={dashboards}
                        cargarTodo={cargarTodo}
                        userRole={userRole}
                        userPermissions={userPermissions}
                        setSubView={setSubView}
                    />
                )}
                {subView === 'email-templates' && canSeeEmailTemplates && (
                    <EmailTemplatesPanel
                        fetchWithAuth={fetchWithAuth}
                        API_URL={API_URL_INTERNAL}
                        user={user}
                    />
                )}
                {subView === 'inventario-reportes' && canSeeInventory && (
                    <ReportInventoryPanel
                        biblioteca={dashboards}
                        clients={clients}
                        projects={projects}
                    />
                )}
                {subView === 'ajustes-reportes' && canSeeAdjustmentsHub && (
                    <ReportAdjustmentsHub
                        dashboards={dashboards}
                        onOpenReport={(report) => {
                            setSelectedReportForDetails(report);
                            setSubView('report-details');
                        }}
                    />
                )}
                {subView === 'repos-social' && userPermissions.repos_social_view && <SocialRepository embedded />}
                {subView === 'repos-perfiles' && userPermissions.repos_social_manage && <SocialProfilesAdmin />}
                {subView === 'creador-reportes' && userPermissions.reports_custom_creator && (
                    <ReportCreatorShell
                        styles={styles}
                        fetchWithAuth={fetchWithAuth}
                        API_URL={API_URL_INTERNAL}
                        clients={clients}
                        projects={projects}
                        refreshAllData={refreshAllData}
                        editorBlocked={narrow && !creatorOnMobile}
                        editorBlockedNotice={(
                            <div style={{ ...studio.card, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <p style={studio.kicker}>Pantalla pequeña</p>
                                <h3 style={{ ...studio.formTitle, margin: 0 }}>El editor está pensado para pantallas grandes</h3>
                                <p style={{ ...studio.hint, lineHeight: 1.5 }}>
                                    Armar un dashboard requiere arrastrar y redimensionar widgets en un lienzo amplio.
                                    Ábrelo desde una computadora o una tablet en horizontal. Los dashboards publicados
                                    sí se ven completos en el teléfono, apilados en una columna.
                                </p>
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    <button type="button" onClick={() => setCreatorOnMobile(true)} style={studio.ghostBtn}>Abrir de todos modos</button>
                                </div>
                            </div>
                        )}
                    />
                )}

            </div>
        </div>
    );
};

const styles = {
    container: { padding: '40px 20px' },
    containerNarrow: { padding: '16px 12px 32px' },
    /* Redes Sociales: la barra fija del módulo va pegada arriba. */
    reposShell: { padding: '0 24px 32px' },
    reposShellNarrow: { padding: '0 12px 28px' },
    creatorShell: {
        padding: '8px 10px',
        flex: 1,
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    creatorWrap: {
        maxWidth: 'none',
        width: '100%',
        margin: 0,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    creatorTitle: {
        margin: '0 0 8px',
        borderBottom: '1px solid #eee',
        paddingBottom: '6px',
        fontSize: '1.15rem',
        fontWeight: 600,
        color: '#5E35B1',
        flexShrink: 0
    },
    section: { display: 'flex', flexDirection: 'column', gap: '25px' },
    card: { backgroundColor: '#fff', padding: '30px', borderRadius: '12px', borderWidth: 1, borderStyle: 'solid', borderColor: '#dadce0' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', width: '100%' }, // Asegura que el formulario ocupe todo el ancho
    filterContainer: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' },
    input: { ...studio.control },
    eyeBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#5f6368', fontWeight: '600' },
    btnBlue: { padding: '14px', backgroundColor: '#5E35B1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    btnGreen: { padding: '14px', backgroundColor: '#1e8e3e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', gridColumn: 'span 2' },
    btnGray: { padding: '14px', backgroundColor: 'transparent', color: '#5f6368', border: '1px solid #dadce0', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', gridColumn: 'span 2' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px', tableLayout: 'fixed', wordBreak: 'break-all' },
    th: {
        padding: '12px',
        textAlign: 'left',
        backgroundColor: '#f3e5f5', // Light purple
        color: '#4a148c', // Dark purple text
        fontWeight: '600',
        fontSize: '0.9rem',
        borderBottom: '2px solid #e1bee7'
    },    
    tr: { borderBottom: '1px solid #e8eaed', textAlign: 'left', fontSize: '0.9rem' },
    // Estilos movidos a la sección global para ser compartidos
    cellEllipsis: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '10px' },
    sBtn: { 
        marginRight: '5px', padding: '0', width: '35px', height: '35px',
        backgroundColor: 'transparent', color: '#5E35B1', 
        border: '1px solid #dadce0', borderRadius: '50%', 
        cursor: 'pointer', fontWeight: '500', fontSize: '1rem',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
    },
    sBtnR: { 
        padding: '0', width: '35px', height: '35px',
        backgroundColor: 'transparent', color: '#E91E63', 
        border: '1px solid #dadce0', borderRadius: '50%', 
        cursor: 'pointer', fontWeight: '500', fontSize: '1rem',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
    },
    sBtnView: { 
        marginRight: '5px', padding: '0', width: '35px', height: '35px',
        backgroundColor: 'transparent', color: '#5E35B1', 
        border: '1px solid #dadce0', borderRadius: '50%', 
        cursor: 'pointer', fontWeight: '500', fontSize: '1rem',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
    },
    miniProfileImage: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        objectFit: 'cover',
        verticalAlign: 'middle',
        border: '1px solid #eee'
    },
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000
    },
    modalContent: {
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        width: '95%', // Make it wider
        maxWidth: '900px', // Increase max-width
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
    },
    userCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px',
        backgroundColor: 'transparent',
        borderBottom: '1px solid #f1f3f4',
        borderRadius: '0',
        cursor: 'pointer'
    },
    treeClient: {
        fontWeight: '600',
        fontSize: '1.1rem',
        color: '#5E35B1',
        cursor: 'pointer',
        padding: '8px 10px',
        backgroundColor: '#f3e5f5',
        borderRadius: '6px'
    },
    minimalistTreeClient: {
        fontWeight: '600',
        fontSize: '1.1rem',
        color: '#202124',
        cursor: 'pointer',
        padding: '8px 0',
        borderBottom: '1px solid #e8eaed'
    },
    companyHeader: {
        color: '#5f6368',
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        cursor: 'pointer'
    },
    treeProject: {
        fontWeight: '500',
        cursor: 'pointer',
        padding: '6px 8px',
        listStyle: 'none'
    },
    minimalistButton: {
        padding: '4px 12px',
        backgroundColor: '#f1f3f4',
        color: '#3c4043',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#dadce0',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500',
        fontSize: '0.85rem',
        flexShrink: 0,
        transition: 'background-color 0.2s',
    },
    minimalistTreeProject: { // Este estilo estaba duplicado, lo mantengo para evitar errores si se usaba en otro lugar
        fontWeight: '500',
        cursor: 'pointer',
        padding: '6px 8px',
        listStyle: 'none'
    },
    treeReportItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 5px',
        borderBottom: '1px solid #f1f3f4'
    },
    treeAccessTypeSelect: {
        padding: '6px 8px', borderRadius: '6px', border: '1px solid #ccc', marginLeft: '15px'
    },
    commentQuoteBlock: {
        padding: '8px 12px',
        margin: '0 0 8px 0',
        backgroundColor: '#f8f9fa',
        borderLeft: '3px solid #5E35B1',
        fontSize: '0.85rem',
        color: '#5f6368',
        borderRadius: '0 4px 4px 0',
        cursor: 'pointer'
    },
    commentBubble: {
        backgroundColor: '#f1f3f4',
        borderRadius: '15px',
        padding: '10px 15px',
    },
    myCommentBubble: {
        backgroundColor: '#d1c4e9', // Púrpura claro para los comentarios del usuario actual
    },
};

/*
 * Ajustes móviles de los estilos compartidos del panel (se pasan como `styles` a
 * modales y vistas hijas). Se aplican al cargar el módulo en pantallas angostas:
 * formularios a una columna, tablas desplazables y modales como hoja inferior.
 */
if (isNarrowViewport()) {
    Object.assign(styles.card, { padding: '16px' });
    Object.assign(styles.formGrid, { gridTemplateColumns: 'minmax(0, 1fr)' });
    Object.assign(styles.filterContainer, { gridTemplateColumns: 'minmax(0, 1fr)' });
    Object.assign(styles.btnGreen, { gridColumn: 'auto' });
    Object.assign(styles.btnGray, { gridColumn: 'auto' });
    Object.assign(styles.table, { display: 'block', overflowX: 'auto', tableLayout: 'auto', wordBreak: 'normal' });
    Object.assign(styles.sBtn, { width: '40px', height: '40px' });
    Object.assign(styles.sBtnR, { width: '40px', height: '40px' });
    Object.assign(styles.sBtnView, { width: '40px', height: '40px' });
    Object.assign(styles.modalOverlay, { alignItems: 'flex-end' });
    Object.assign(styles.modalContent, {
        width: '100%',
        maxWidth: '100%',
        maxHeight: '88vh',
        padding: '18px 16px calc(18px + env(safe-area-inset-bottom))',
        borderRadius: '16px 16px 0 0',
        boxSizing: 'border-box',
    });
}

// Helper para generar una lista de todos los permisos posibles (para RoleManagement)
// En un sistema real, esto podría cargarse desde un archivo de configuración o un endpoint.
const generateAllPermissionsList = () => {
    return [
        { id: 'admin_panel_access', name: 'Acceso al Panel de Administración', category: 'Panel Admin' },
        { id: 'admin_panel_users_view', name: 'Ver Gestión de Usuarios', category: 'Panel Admin' },
        { id: 'admin_panel_groups_view', name: 'Ver Gestión de Grupos', category: 'Panel Admin' },
        { id: 'admin_panel_roles_view', name: 'Ver Gestión de Roles', category: 'Panel Admin' },
        { id: 'admin_panel_library_view', name: 'Ver Biblioteca de Reportes (Completa)', category: 'Panel Admin' },
        { id: 'admin_panel_library_view_own', name: 'Ver Biblioteca de Reportes (Propios)', category: 'Panel Admin' },
        { id: 'admin_panel_permissions_view', name: 'Ver Gestión de Accesos', category: 'Panel Admin' },
        { id: 'admin_panel_clients_projects_view', name: 'Ver Clientes y Proyectos', category: 'Panel Admin' },
        { id: 'admin_panel_audit_view', name: 'Ver Auditoría de Datos de Reportes', category: 'Panel Admin' },
        { id: 'admin_panel_comprehensive_audit_view', name: 'Ver Auditoría de Permisos', category: 'Panel Admin' },
        { id: 'admin_panel_statistics_view', name: 'Ver Panel de Estadísticas', category: 'Panel Admin' }, // Nuevo permiso

        { id: 'users_create', name: 'Crear Usuarios', category: 'Usuarios' },
        { id: 'users_edit', name: 'Editar Usuarios', category: 'Usuarios' },
        { id: 'users_delete', name: 'Eliminar Usuarios', category: 'Usuarios' },
        { id: 'users_impersonate', name: 'Suplantar Usuarios', category: 'Usuarios' },
        { id: 'users_assign_admin_pm_roles', name: 'Asignar roles Admin/PM', category: 'Usuarios' },
        { id: 'users_view_admin_pm', name: 'Ver usuarios Admin/PM', category: 'Usuarios' },
        { id: 'users_view_all', name: 'Ver todos los usuarios', category: 'Usuarios' },

        { id: 'groups_create', name: 'Crear Grupos', category: 'Grupos' },
        { id: 'groups_edit', name: 'Editar Grupos', category: 'Grupos' },
        { id: 'groups_delete', name: 'Eliminar Grupos', category: 'Grupos' },
        { id: 'groups_manage_members', name: 'Gestionar Miembros de Grupo', category: 'Grupos' },
        { id: 'groups_manage_permissions', name: 'Gestionar Permisos de Grupo', category: 'Grupos' },
        { id: 'groups_view_all', name: 'Ver todos los grupos', category: 'Grupos' },

        { id: 'roles_create', name: 'Crear Roles', category: 'Roles' },
        { id: 'roles_edit', name: 'Editar Roles', category: 'Roles' },
        { id: 'roles_delete', name: 'Eliminar Roles', category: 'Roles' },
        { id: 'roles_view_all', name: 'Ver todos los roles', category: 'Roles' },

        { id: 'clients_create', name: 'Crear Clientes', category: 'Clientes y Proyectos' },
        { id: 'clients_edit', name: 'Editar Clientes', category: 'Clientes y Proyectos' },
        { id: 'clients_delete', name: 'Eliminar Clientes', category: 'Clientes y Proyectos' },
        { id: 'clients_view_all', name: 'Ver todos los clientes', category: 'Clientes y Proyectos' },

        { id: 'projects_create', name: 'Crear Proyectos', category: 'Clientes y Proyectos' },
        { id: 'projects_edit', name: 'Editar Proyectos', category: 'Clientes y Proyectos' },
        { id: 'projects_delete', name: 'Eliminar Proyectos', category: 'Clientes y Proyectos' },
        { id: 'projects_view_all', name: 'Ver todos los proyectos', category: 'Clientes y Proyectos' },

        { id: 'reports_create', name: 'Crear Reportes Maestros', category: 'Reportes' },
        { id: 'reports_edit', name: 'Editar Reportes Maestros', category: 'Reportes' },
        { id: 'reports_delete', name: 'Eliminar Reportes Maestros', category: 'Reportes' },
        { id: 'reports_view_views', name: 'Ver Detalle de Vistas de Reportes', category: 'Reportes' },
        { id: 'reports_view_statistics', name: 'Ver Estadísticas de Vistas de Reportes', category: 'Reportes' }, // Nuevo permiso
        { id: 'reports_view_approval_status', name: 'Ver Estado de Aprobación de Reportes', category: 'Reportes' }, // Nuevo permiso
        { id: 'reports_view_responsibles', name: 'Ver Responsables de Reportes', category: 'Reportes' }, // Nuevo permiso
        { id: 'reports_manage_responsibles', name: 'Gestionar Responsables de Reportes', category: 'Reportes' }, // Nuevo permiso
        { id: 'reports_lock_approval', name: 'Bloquear o desbloquear aprobación de reportes', category: 'Reportes' },
        { id: 'reports_view_access_users', name: 'Ver Usuarios con Acceso a Reportes', category: 'Reportes' },
        { id: 'reports_custom_creator', name: 'Utilizar Creador de Reportes Python', category: 'Reportes' },
        { id: 'repos_social_view', name: 'Consultar repositorio de Redes Sociales', category: 'Repositorios' },
        { id: 'repos_social_manage', name: 'Administrar perfiles visibles de Redes Sociales', category: 'Repositorios' },
        { id: 'office_world_view', name: 'Ver la Oficina Pixel (animación de oficina en Inicio)', category: 'Inicio' },
        { id: 'reports_view_all', name: 'Ver todos los reportes maestros', category: 'Reportes' },
        { id: 'reports_upload_files', name: 'Cargar Archivos a reporte', category: 'Reportes' },

        { id: 'maintenance_execute_tasks', name: 'Ejecutar Tareas de Mantenimiento', category: 'Mantenimiento' },
        { id: 'maintenance_restore', name: 'Restaurar base de datos desde un respaldo', category: 'Mantenimiento' },
        { id: 'admin_panel_email_templates', name: 'Administrar plantillas y cuentas de correo', category: 'Mantenimiento' },
        { id: 'admin_panel_report_inventory', name: 'Ver inventario de reportes', category: 'Mantenimiento' },

        { id: 'reports_manage_adjustments', name: 'Gestionar ajustes solicitados en reportes', category: 'Reportes' },
        { id: 'reports_manage_comments', name: 'Gestionar Comentarios de Reportes', category: 'Reportes' },
        { id: 'permissions_assign_direct', name: 'Asignar Permisos Directos', category: 'Accesos' },
        { id: 'permissions_update_access_type', name: 'Actualizar Tipo de Acceso Directo', category: 'Accesos' },
        { id: 'permissions_delete', name: 'Eliminar Permisos Directos', category: 'Accesos' },
        { id: 'permissions_view_all', name: 'Ver todos los permisos directos', category: 'Accesos' }
    ];
};

export default AdminPanel;
